import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Phone, X, ArrowLeft } from "lucide-react";

export default function PhoneMode() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [script, setScript] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/prospects/${id}`);
      setP(data.prospect);
      try {
        const r = await api.post("/ai/phone-script", { lead: data.prospect });
        setScript(r.data);
      } catch (_) { /* swallow: non-fatal */ }
    })();
  }, [id]);

  const submitResult = async (type) => {
    let status = p.status;
    let outcome = type;
    if (type === "rdv") { status = "RDV planifié"; outcome = "RDV décroché"; }
    else if (type === "recall") { status = "Contacté"; outcome = "À rappeler"; }
    else if (type === "no") { status = "Sans suite"; outcome = "Pas intéressé"; }
    await api.post("/interactions", { prospect_id: id, type: "call", outcome });
    await api.put(`/prospects/${id}`, { status });
    toast.success("Résultat enregistré");
    setResult(type);
  };

  if (!p) return <div className="min-h-screen grid place-items-center bg-[var(--surface)]"><div className="w-8 h-8 rounded-full border-2 border-emerald-500 border-t-transparent animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-[var(--surface-alt)] pb-8">
      <div className="bg-[rgba(9,9,11,0.85)] backdrop-blur-md border-b p-4 flex items-center gap-3 sticky top-0 z-10">
        <button onClick={() => navigate(-1)} className="p-2"><ArrowLeft className="w-5 h-5" /></button>
        <div className="flex-1">
          <div className="font-display font-bold text-lg text-[var(--text)]">{p.name}</div>
          <div className="text-xs text-zinc-500">{p.company}</div>
        </div>
        {p.phone && <a href={`tel:${p.phone}`} className="bg-emerald-500/100/100 text-white rounded-full px-4 py-2 text-sm font-bold flex items-center gap-1.5" data-testid="call-btn"><Phone className="w-4 h-4" />Appeler</a>}
      </div>

      {!script ? (
        <div className="grid place-items-center py-12"><div className="w-8 h-8 rounded-full border-2 border-emerald-500 border-t-transparent animate-spin" /></div>
      ) : (
        <div className="max-w-2xl mx-auto p-4 space-y-4">
          <div className="bg-emerald-500/100/10 rounded-2xl p-4 border border-emerald-500/30">
            <div className="text-xs font-bold uppercase tracking-wider text-emerald-300">🎯 Objectif</div>
            <div className="text-base text-zinc-200 mt-1">{script.objectif}</div>
            <div className="text-xs text-zinc-500 mt-2">⏰ Meilleur moment : {script.meilleur_moment}</div>
          </div>

          <div className="bg-[var(--surface)] rounded-xl p-4 border border-[var(--border-soft)] shadow-sm">
            <div className="text-xs font-bold uppercase tracking-wider text-zinc-500">💬 Étape 1 : Accroche</div>
            <div className="text-base text-zinc-200 mt-1 leading-relaxed" data-testid="script-intro">{script.accroche}</div>
          </div>

          <div className="bg-[var(--surface)] rounded-xl p-4 border border-[var(--border-soft)] shadow-sm">
            <div className="text-xs font-bold uppercase tracking-wider text-zinc-500">Étape 2 : Qualification</div>
            <ul className="mt-2 space-y-2">
              {(script.qualification || []).map((q, i) => (
                <li key={i} className="text-sm text-zinc-200 flex gap-2"><span className="text-emerald-400 font-bold">{i + 1}.</span>{q}</li>
              ))}
            </ul>
          </div>

          <div className="bg-[var(--surface)] rounded-xl p-4 border border-[var(--border-soft)] shadow-sm">
            <div className="text-xs font-bold uppercase tracking-wider text-zinc-500">Étape 3 : Closing</div>
            <div className="text-base text-zinc-200 mt-1">{script.closing}</div>
          </div>

          {script.objections?.length > 0 && (
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">⚠️ Objections</div>
              <div className="space-y-2">
                {script.objections.map((o, i) => (
                  <details key={i} className="bg-[var(--surface)] rounded-xl border border-[var(--border-soft)] shadow-sm">
                    <summary className="p-3 font-semibold text-sm cursor-pointer list-none">{o.objection}</summary>
                    <div className="p-3 pt-0 text-sm text-zinc-300">{o.reponse}</div>
                  </details>
                ))}
              </div>
            </div>
          )}

          <div className="bg-[var(--surface)] rounded-xl p-4 border border-[var(--border-soft)] shadow-sm">
            <div className="text-sm font-semibold text-[var(--text)] mb-3">Résultat de l'appel</div>
            <div className="grid grid-cols-2 gap-2">
              <Button onClick={() => submitResult("rdv")} data-testid="result-rdv" className="rounded-full bg-emerald-500/100/100 hover:bg-emerald-600 h-11 font-semibold">✅ RDV décroché</Button>
              <Button onClick={() => submitResult("recall")} data-testid="result-recall" className="rounded-full bg-amber-500/100 hover:bg-amber-600 h-11 font-semibold">🔁 À rappeler</Button>
              <Button onClick={() => submitResult("no")} data-testid="result-no" variant="outline" className="rounded-full h-11 font-semibold">❌ Pas intéressé</Button>
              <Button onClick={() => submitResult("other")} data-testid="result-other" variant="outline" className="rounded-full h-11 font-semibold">📝 Autre</Button>
            </div>
            {result && <div className="mt-3 text-sm text-emerald-300">✅ Résultat enregistré. <button onClick={() => navigate("/pipeline")} className="underline font-semibold">Voir le pipeline</button></div>}
          </div>
        </div>
      )}
    </div>
  );
}
